import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "reactstrap";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import RichTable, { RichColumn } from "src/components/Page/RichTable";
import { useAppDispatch } from "src/store";
import {
  getOntologyCoverage,
  getOntologyOverview,
  getSchemaSectors,
  getUnmappedGroups,
} from "src/store/ontology/saga";
import {
  OntologyOverview,
  SchemaSector,
  SectorCoverage,
  UnmappedGroup,
} from "src/store/ontology/type";
import CoverageDetailModal from "./detail";
import MapGroupModal from "./map";

/**
 * Ontoloji yönetimi — V2 Faz F.
 *
 * Ontoloji artık KT veritabanında yaşıyor (CategorySchemas / SectorSchemas /
 * AttributeDefinitions) ve buradan yönetiliyor. Rentificial Ai'daki ayrı
 * yönetim yüzeyi kaldırıldı.
 *
 * Ekranın asıl işi sayı göstermek değil, iki sessiz veri kaybını görünür
 * kılmak: eşlenmemiş kategorideki ürünler aramaya hiç çıkmaz, özelliği
 * girilmemiş ürünler filtrelerde görünmez.
 */
const OntologyPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [overview, setOverview] = useState<OntologyOverview>();
  const [sectors, setSectors] = useState<SchemaSector[]>([]);
  const [coverage, setCoverage] = useState<SectorCoverage[]>([]);
  const [unmapped, setUnmapped] = useState<UnmappedGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"coverage" | "sectors" | "unmapped">("coverage");
  const [mapTarget, setMapTarget] = useState<UnmappedGroup>();
  const [detailTarget, setDetailTarget] = useState<SectorCoverage>();

  document.title = t("Ontology.MetaTag", "Ontoloji | Rentificial") as string;

  const fetchAll = () => {
    setLoading(true);
    dispatch(getOntologyOverview({ payload: { onSuccess: (_m: any, p: OntologyOverview) => setOverview(p), onError: () => {} } }));
    dispatch(getSchemaSectors({ payload: { onSuccess: (_m: any, p: SchemaSector[]) => setSectors(p ?? []), onError: () => {} } }));
    dispatch(getUnmappedGroups({ payload: { onSuccess: (_m: any, p: UnmappedGroup[]) => setUnmapped(p ?? []), onError: () => {} } }));
    dispatch(
      getOntologyCoverage({
        payload: {
          onSuccess: (_m: any, p: SectorCoverage[]) => {
            setCoverage(p ?? []);
            setLoading(false);
          },
          onError: () => setLoading(false),
          onFinally: () => setLoading(false),
        },
      })
    );
  };

  useEffect(fetchAll, [dispatch]);

  const coverageColumns: RichColumn<SectorCoverage>[] = [
    { key: "SectorRef", label: t("Ontology.SectorRef", "Kod") },
    { key: "Title", label: t("Ontology.Sector", "Sektör") },
    { key: "CategoryCount", label: t("Ontology.Categories", "Kategori") },
    { key: "CardCount", label: t("Ontology.Cards", "Ürün") },
    {
      key: "CardsWithAttributes",
      label: t("Ontology.WithAttributes", "Özelliği girilmiş"),
      render: (r) => {
        if (r.CardCount === 0) return <span className="text-muted">—</span>;
        const pct = Math.round((r.CardsWithAttributes / r.CardCount) * 100);
        const cls = pct >= 80 ? "success" : pct >= 30 ? "warning" : "danger";
        return (
          <span className={`badge bg-${cls}`}>
            {r.CardsWithAttributes} / {r.CardCount} (%{pct})
          </span>
        );
      },
    },
  ];

  const sectorColumns: RichColumn<SchemaSector>[] = [
    { key: "ParentSector", label: t("Ontology.SectorRef", "Kod") },
    { key: "Name", label: t("Ontology.Sector", "Sektör") },
    { key: "CategoryCount", label: t("Ontology.Categories", "Kategori") },
    { key: "CurrentVersion", label: t("Ontology.Version", "Sürüm") },
    {
      key: "IsWedge",
      label: t("Ontology.Wedge", "Odak"),
      render: (r) => (r.IsWedge ? <span className="badge bg-primary">wedge</span> : <span className="text-muted">—</span>),
    },
  ];

  const unmappedColumns: RichColumn<UnmappedGroup>[] = [
    { key: "Name", label: t("Ontology.GroupName", "Kategori") },
    { key: "ClientName", label: t("Ontology.Client", "Firma") },
    { key: "AssetTypeName", label: t("Ontology.AssetType", "Tip") },
    {
      key: "CardCount",
      label: t("Ontology.HiddenCards", "Görünmeyen ürün"),
      render: (r) => <span className="badge bg-danger">{r.CardCount}</span>,
    },
  ];

  const Kpi = ({ label, value, tone }: { label: string; value: number | string; tone?: string }) => (
    <div className="card mb-0" style={{ minWidth: 170, flex: "1 1 170px" }}>
      <div className="card-body py-3">
        <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
        <div className={`fw-bold ${tone ? "text-" + tone : ""}`} style={{ fontSize: 24 }}>{value}</div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <Breadcrumbs
              title={t("Breadcrumb.Dashboard")}
              breadcrumbItem={t("Ontology.PageTitle", "Ontoloji") as string}
            />
            <button className="btn btn-warning mb-3" onClick={fetchAll}>
              <i className="mdi mdi-refresh" />
            </button>
          </div>

          {overview && (
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Kpi label={t("Ontology.Sectors", "Sektör") as string} value={overview.Sectors} />
              <Kpi label={t("Ontology.Categories", "Kategori") as string} value={overview.Categories} />
              <Kpi label={t("Ontology.Attributes", "Özellik tanımı") as string} value={overview.Attributes} />
              <Kpi
                label={t("Ontology.InvisibleCards", "Aramada görünmeyen ürün") as string}
                value={overview.InvisibleCards}
                tone={overview.InvisibleCards > 0 ? "danger" : "success"}
              />
              <Kpi
                label={t("Ontology.NoAttributes", "Özelliği girilmemiş ürün") as string}
                value={overview.CardsWithoutAttributes}
                tone={overview.CardsWithoutAttributes > 0 ? "warning" : "success"}
              />
            </div>
          )}

          {overview && overview.InvisibleCards > 0 && (
            <div className="alert alert-danger py-2">
              {t(
                "Ontology.InvisibleWarning",
                "Eşlenmemiş kategorideki ürünler aramaya hiç çıkmıyor. Aşağıdaki 'Eşlenmemiş' sekmesinden kategorileri bağlayın."
              )}
            </div>
          )}

          <ul className="nav nav-pills mb-3 gap-1">
            <li className="nav-item">
              <button type="button" className={`nav-link ${tab === "coverage" ? "active" : ""}`} onClick={() => setTab("coverage")}>
                {t("Ontology.TabCoverage", "Kapsam")}
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${tab === "sectors" ? "active" : ""}`} onClick={() => setTab("sectors")}>
                {t("Ontology.TabSectors", "Sektörler")}
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${tab === "unmapped" ? "active" : ""}`} onClick={() => setTab("unmapped")}>
                {t("Ontology.TabUnmapped", "Eşlenmemiş")}{" "}
                {unmapped.length > 0 && <span className="badge bg-danger ms-1">{unmapped.length}</span>}
              </button>
            </li>
          </ul>

          {tab === "coverage" && (
            <RichTable<SectorCoverage>
              tableId="ontology-coverage"
              t={t as any}
              columns={coverageColumns}
              rows={coverage}
              loading={loading}
              rowKey={(r) => r.SectorRef}
              emptyText={t("Ontology.Empty", "Kayıt yok") as string}
              actions={(r) => (
                <button
                  className="btn btn-sm btn-primary"
                  disabled={r.CategoryCount === 0}
                  onClick={() => setDetailTarget(r)}
                >
                  {t("Ontology.Detail", "Detay")}
                </button>
              )}
            />
          )}

          {tab === "sectors" && (
            <RichTable<SchemaSector>
              tableId="ontology-sectors"
              t={t as any}
              columns={sectorColumns}
              rows={sectors}
              loading={loading}
              rowKey={(r) => r.ParentSector}
              emptyText={t("Ontology.Empty", "Kayıt yok") as string}
            />
          )}

          {tab === "unmapped" && (
            <RichTable<UnmappedGroup>
              tableId="ontology-unmapped"
              t={t as any}
              columns={unmappedColumns}
              rows={unmapped}
              loading={loading}
              rowKey={(r) => r.Id}
              emptyText={t("Ontology.NoUnmapped", "Eşlenmemiş kategori yok") as string}
              actions={(r) => (
                <button className="btn btn-sm btn-primary" onClick={() => setMapTarget(r)}>
                  {t("Ontology.MapButton", "Eşle")}
                </button>
              )}
            />
          )}
        </Container>
      </div>

      {detailTarget && (
        <CoverageDetailModal
          sector={detailTarget}
          open={!!detailTarget}
          onHide={() => setDetailTarget(undefined)}
        />
      )}

      {mapTarget && (
        <MapGroupModal
          group={mapTarget}
          sectors={sectors}
          open={!!mapTarget}
          onHide={() => setMapTarget(undefined)}
          onMapped={() => {
            setMapTarget(undefined);
            fetchAll();
          }}
        />
      )}
    </React.Fragment>
  );
};

export default OntologyPage;
